import { useEffect, useRef, useState } from 'react';

// 따라잡은 상태의 기본 타이핑 속도.
const DEFAULT_CHARS_PER_SEC = 30;
// 밀린 글자당 초당 추가 속도 → speed = base + gain·backlog (적응형 가속).
// 0 이면 가속 없이 항상 고정 속도(charsPerSecond)로만 타이핑한다.
const DEFAULT_BACKLOG_GAIN = 0;

type UseAiBuilderTypewriterParams = {
  fullText: string;
  enabled: boolean;
  // 타이핑 속도 튜닝(선택). 기본값은 프로덕션 상수. 주로 Storybook/실험용.
  charsPerSecond?: number;
  backlogGain?: number;
};

// SSE text_delta 도착 속도와 분리해, 클라이언트 rAF 클럭으로 타이핑 속도를 통제한다.
// fullText(누적 전체 텍스트)를 타깃으로 두고 표시 커서를 그 길이까지 전진시킨다.
//
// 설계 포인트
// - 지속 루프: fullText 를 ref 로 읽어 delta 마다 effect 를 재시작하지 않는다.
//   (재시작하면 버스트 도착 시 rAF 가 취소·리셋되며 커서가 튄다)
// - 소수점 누적: 진행을 float 로 쌓아 낮은 charsPerSecond 도 정확히 반영한다.
//   (프레임당 정수 올림을 하면 저속이 무력화된다)
// - passthrough: 스트리밍 대상이 아닌 채로 마운트되면(완성/복원 메시지)
//   타이핑 없이 전문을 그대로 반환한다. 재타이핑이 없다.
// - 일관 속도: 턴이 끝나(enabled=false) 도 같은 charsPerSecond 로 남은 버퍼를
//   마저 찍는다(전문 스냅 없음). charsPerSecond 가 유일한 속도 손잡이다.
export const useAiBuilderTypewriter = ({
  fullText,
  enabled,
  charsPerSecond = DEFAULT_CHARS_PER_SEC,
  backlogGain = DEFAULT_BACKLOG_GAIN,
}: UseAiBuilderTypewriterParams): string => {
  // 마운트 시 스트리밍 대상이 아니면 전문 통과(타이핑 안 함).
  const isPassthroughRef = useRef(!enabled);
  const [shownCount, setShownCount] = useState(0);
  const progressRef = useRef(0); // 소수점 포함 진행 위치(글자 수)
  const fullTextRef = useRef(fullText);
  fullTextRef.current = fullText;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  useEffect(() => {
    if (isPassthroughRef.current) return;

    let rafId = 0;
    let lastTimestamp: number | null = null;

    const tick = (timestamp: number) => {
      const elapsedSec =
        lastTimestamp === null ? 0 : (timestamp - lastTimestamp) / 1000;
      lastTimestamp = timestamp;

      const target = fullTextRef.current.length;
      const current = progressRef.current;
      const backlog = target - current;

      if (backlog > 0) {
        const speed = charsPerSecond + backlogGain * backlog;
        const next = Math.min(target, current + speed * elapsedSec);
        progressRef.current = next;

        const nextShown = Math.floor(next);
        setShownCount(previous =>
          previous === nextShown ? previous : nextShown,
        );
      }

      // enabled 인 동안엔 새 delta 를 기다리며 idle 유지, enabled 아니어도
      // 아직 덜 찍었으면 drain 을 마저 돌린 뒤 정지한다.
      if (
        enabledRef.current ||
        progressRef.current < fullTextRef.current.length
      ) {
        rafId = requestAnimationFrame(tick);
      }
    };

    rafId = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(rafId);
    // fullText 는 deps 에 넣지 않는다(ref 로 읽음). enabled 전환(스트리밍→종료)
    // 때만 루프를 재구성해 drain 으로 넘어간다.
  }, [enabled, charsPerSecond, backlogGain]);

  return isPassthroughRef.current ? fullText : fullText.slice(0, shownCount);
};
